import { useState } from "react";
import {
  Action,
  ActionButtonGroup,
  Banner,
  Box,
  Dialog,
  FeedbackBlock,
  Heading,
  LoadingStatusProvider,
  LocalizedString,
  PanelHeaderIcon,
  Space,
  SpinnerIcon,
  Stack,
  useIsMobileLayout,
  useIsTouchScreen,
} from "@dbn/omnichannel-design-system-g4p";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { SendContractsViaEmailDialog } from "./SendContractsViaEmailDialog";
import { useAppContext } from "../../useAppContext";
import { PrintDocumentsDialog } from "./PrintDocumentsDialog";
import { array, option } from "fp-ts";
import { Option } from "fp-ts/Option";
import { pipe } from "fp-ts/function";
import { MainContent } from "../../Common/MainContent";
import { SecondaryButtonStyled } from "./SummaryAndDownloadCommonStyles.css";

type Props = {
  stepNo: number;
  finalChecksComplete: boolean;
  promotion: Option<LocalizedString>;
  heading: {
    title: LocalizedString;
    description: LocalizedString;
  };
};

export function Summary(props: Props) {
  const [isPrintContractsVisible, setIsPrintContractsVisible] = useState(false);
  const [isEmailContractsVisible, setIsEmailContractsVisible] = useState(false);
  const isMobileLayout = useIsMobileLayout();

  const formatMessage = useFormatMessage();

  const {
    config: { externalCommunication },
  } = useAppContext();

  const isTouchScreen = useIsTouchScreen();

  const showPrintContracts = (): void => {
    setIsPrintContractsVisible(true);
  };

  const showEmailContracts = (): void => {
    setIsEmailContractsVisible(true);
  };

  const hidePrintContractsModal = (): void => {
    setIsPrintContractsVisible(false);
  };

  const hideEmailContractsModal = (): void => {
    setIsEmailContractsVisible(false);
  };

  const printDocumentsDialog = props.finalChecksComplete ? (
    <PrintDocumentsDialog onDismiss={hidePrintContractsModal} />
  ) : (
    <Dialog
      ariaLabelMessageForCloseIcon={formatMessage(
        "Modal.closeButton" as RuntimeLocaleKey
      )}
      size="medium"
      variant="center"
      icon={SpinnerIcon}
      title={formatMessage("SummaryAndDownload.Summary.waiting.title")}
      subtitle={formatMessage("SummaryAndDownload.Summary.waiting.subTitle")}
      onDismiss={option.some(hidePrintContractsModal)}
      actions={[]}
    />
  );

  const renderActionButtonGroup = (
    <div className={SecondaryButtonStyled}>
      <ActionButtonGroup
        actions={
          array.compact([
            option.fromPredicate(() => externalCommunication)({
              label: formatMessage(
                "SummaryAndDownload.Summary.email.contracts.label"
              ),
              variant: "secondary",
              action: showEmailContracts,
              disabled: !props.finalChecksComplete,
            }),
            option.fromPredicate(() => !isTouchScreen)({
              label: formatMessage(
                "SummaryAndDownload.Summary.print.contracts.label"
              ),
              variant: "primary",
              action: showPrintContracts,
            }),
          ]) as [Action] | [Action, Action]
        }
      />
    </div>
  );

  const renderLoadingContracts = (
    <FeedbackBlock
      type="loading"
      size="medium"
      heading={formatMessage("SummaryAndDownload.Summary.waiting.title")}
      subheading={option.some(
        formatMessage("SummaryAndDownload.Summary.waiting.subTitle")
      )}
    />
  );

  return (
    <MainContent>
      <Box column grow shrink>
        <Stack
          units={5}
          vAlignContent={isMobileLayout ? "top" : "center"}
          shrink={isMobileLayout}
        >
          <PanelHeaderIcon
            variant="complete"
            type="number"
            value={props.stepNo}
          />
          <Heading size="small" weight="medium">
            {props.heading.title}
          </Heading>
        </Stack>
      </Box>
      <Space units={5} />
      <Box>
        <Box
          shrink
          grow
          style={{ textAlign: "justify", fontSize: "18px", lineHeight: "24px" }}
        >
          {props.heading.description}
        </Box>
      </Box>
      <Space units={5} />
      <Box column={isMobileLayout}>
        {props.finalChecksComplete
          ? renderActionButtonGroup
          : renderLoadingContracts}
      </Box>
      {pipe(
        props.promotion,
        option.map(promotion => (
          <>
            <Space units={10} />
            <Box>
              <Space units={isMobileLayout ? 6 : 21} />
              <Banner
                type="informative"
                title={option.some(
                  formatMessage("SummaryAndDownload.Summary.promotionTitle")
                )}
                content={promotion}
                actions={option.none}
                onDismiss={option.none}
              />
            </Box>
          </>
        )),
        option.toNullable
      )}
      <LoadingStatusProvider>
        <>
          {isPrintContractsVisible && printDocumentsDialog}
          {isEmailContractsVisible && (
            <SendContractsViaEmailDialog
              onCancel={hideEmailContractsModal}
              onConfirm={hideEmailContractsModal}
            />
          )}
        </>
      </LoadingStatusProvider>
    </MainContent>
  );
}
